import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormType } from '../../../api/forms';
import { isApiError } from '../../../api/errors';
import { downloadTemplateVersion } from '../../../api/templates';
import { Button } from '../../../components/idsk';
import { useAnnouncer } from '../../../layout/Announcer';

/**
 * "Exportovať do JSON": downloads the version as a template file (D32). The accessible name says which version of
 * which template; progress and the result are announced, a failure is shown next to the button.
 */
export function ExportTemplateButton({ type, versionNo, name, variant = 'button' }: { type: FormType; versionNo: number; name: string; variant?: 'button' | 'link' }) {
  const { t } = useTranslation();
  const { announce, alert } = useAnnouncer();
  const errorId = useId();
  const busy = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const what = t('templates.export.what', { n: versionNo, name });

  const run = async () => {
    if (busy.current) return;
    busy.current = true;
    setError(null);
    announce(t('templates.export.downloading', { what }));
    try {
      await downloadTemplateVersion(type, versionNo);
      announce(t('templates.export.downloaded', { what }));
    } catch (e) {
      const text = isApiError(e) ? e.message : t('templates.export.failed');
      setError(text);
      alert(text);
    } finally {
      busy.current = false;
    }
  };

  const label = (
    <>
      {variant === 'link' ? t('templates.export.short') : t('templates.export.button')} <span className="govuk-visually-hidden">{t('templates.export.hidden', { what })}</span>
    </>
  );
  return (
    <>
      {variant === 'link' ? (
        <button type="button" className="govuk-link app-link-button" onClick={() => void run()} aria-describedby={error ? errorId : undefined}>
          {label}
        </button>
      ) : (
        <Button variant="secondary" onClick={() => void run()} aria-describedby={error ? errorId : undefined}>
          {label}
        </Button>
      )}
      {error && (
        <p className="govuk-error-message" id={errorId}>
          <span className="govuk-visually-hidden">{t('common.errorPrefix')}</span> {error}
        </p>
      )}
    </>
  );
}
